import type { TaskApiId, TaskV1 } from '@/model/Task.model';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';
import { TZDate } from '@date-fns/tz';
import { isAfter, isBefore, parse } from 'date-fns';

export type TaskPosition = {
  id: TaskApiId;
  row: number;
  start: number;
  end: number;
  duration: number;
};
export const retry = <T>(action: () => Promise<T>, maxRetries: number = 3): Promise<T> => {
  return action().catch((err) => {
    if (maxRetries <= 1) {
      return Promise.reject(err);
    }
    return retry(action, maxRetries - 1);
  });
};

/**
 * Packs tasks into a map of task positions.
 *
 * Varient of [ISMP](https://en.wikipedia.org/wiki/Interval_scheduling) algorithm for packing tasks into rows.
 *
 * @param {Array<TaskV1>} tasks - The array of tasks to be packed.
 * @param {string} packDate - The date to pack the tasks for.
 * @param {string} packTimezone - The timezone to use for packing the tasks.
 * @returns {Map<string, TaskPosition>} - A map of task grid positions.
 */
export const packTasks = (tasks: Array<TaskV1>, packDate: string, packTimezone: string): Map<string, TaskPosition> => {
  const parsedPackDate = parse(packDate, 'yyyy-MM-dd', new Date());

  const zonedStartOfDay = new TZDate(
    parsedPackDate.getFullYear(),
    parsedPackDate.getMonth(),
    parsedPackDate.getDate(),
    0,
    0,
    0,
    packTimezone
  );
  const zonedEndOfDay = new TZDate(
    parsedPackDate.getFullYear(),
    parsedPackDate.getMonth(),
    parsedPackDate.getDate(),
    23,
    59,
    59,
    packTimezone
  );

  const { taskPositions } = tasks.reduce<{
    taskPositions: Map<string, TaskPosition>;
  }>(
    (acc, task) => {
      const zonedStartDate = new TZDate(task.duration.start, packTimezone);
      const zonedEndDate = new TZDate(task.duration.end, packTimezone);

      const overlapLeft = isBefore(zonedStartDate, zonedStartOfDay);
      const overlapRight = isAfter(zonedEndDate, zonedEndOfDay);

      const startHours = overlapLeft ? 0 : zonedStartDate.getHours();
      const startMinutes = overlapLeft ? 0 : zonedStartDate.getMinutes();
      const startSlot = Math.max(0, startHours * 2 + Math.floor(startMinutes / 30));

      const endHours = overlapRight ? 24 : zonedEndDate.getHours();
      const endMinutes = overlapRight ? 0 : zonedEndDate.getMinutes();

      const endSlot = endHours * 2 + (endMinutes > 0 ? Math.ceil(endMinutes / 30) : 0);

      const duration = Math.max(1, endSlot - startSlot);
      acc.taskPositions.set(task.id, {
        id: task.id,
        row: -1,
        start: startSlot,
        end: Math.min(48, startSlot + duration),
        duration,
      });
      return acc;
    },
    { taskPositions: new Map<string, TaskPosition>() }
  );

  const rows: Array<Array<number>> = [];
  Array.from(taskPositions.values())
    .sort((left, right) => (left.start === right.start ? right.duration - left.duration : left.start - right.start))
    .forEach((taskPosition) => {
      let selectedRow = -1;
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        if (row.every((end) => end <= taskPosition.start)) {
          selectedRow = i;
          break;
        }
      }
      if (selectedRow === -1) {
        selectedRow = rows.length;
        rows.push([taskPosition.end]);
      } else {
        rows[selectedRow].push(taskPosition.end);
      }
      (taskPositions.get(taskPosition.id) as TaskPosition).row = selectedRow;
    });
  return taskPositions;
};
type PageLoadResponse<T> = {
  data: Array<T>;
  page: number;
  last_page: number;
};

type PageLoaderProps<T> = {
  loadPage: (page: number) => Promise<PageLoadResponse<T>>;
};
type PageLoaderResponse<T> = {
  data: Array<T>;
};
export const usePageLoader = <T>({ loadPage }: PageLoaderProps<T>): PageLoaderResponse<T> => {
  const [data, setData] = useState<Record<number, Array<T>>>({});
  const fetch = useCallback(
    (page: number) =>
      retry(() => loadPage(page)).then((response) => {
        setData((previous) => ({ ...previous, [response.page]: response.data }));
        return response;
      }),
    [setData, loadPage]
  );
  const reload = useCallback(async () => {
    try {
      setData({});
      const pageOne = await fetch(1);
      await Promise.allSettled(
        Array.from({ length: (pageOne.last_page ?? 1) - 1 }).map((_, idx) => retry(() => fetch(idx + 2)))
      );
    } catch (cause) {
      ExceptionHandler.captureException(
        new InVivoError('Failed to load all pages', {
          cause,
          slug: 'use-page-loader',
        })
      );
    }
  }, [fetch]);
  useEffect(() => {
    reload();
  }, []);

  return useMemo(
    () => ({
      reload,
      data: Object.entries(data)
        .sort((left, right) => Number(left[0]) - Number(right[0]))
        .flatMap((entry) => entry[1]),
    }),
    [data, reload]
  );
};
